import { useState } from 'react';
import { ArrowRight, KeyRound, ShieldCheck } from 'lucide-react';
import { ApiClientError, LogoMark, Spinner } from '@riskpay/ui-components';
import { authApi, useAuth } from '../lib/client';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string; merchant_id?: string; totp_enabled?: boolean };
}

/**
 * Sign in.
 *
 * The TOTP field appears only after the API says it is needed
 * (`two_factor_required`), rather than being shown to everyone up front — most
 * merchant users have not enabled 2FA, and an always-visible empty code box
 * reads as an error state.
 */
export function SignIn() {
  const signIn = useAuth((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await authApi.post<LoginResponse>('/employee/login', {
        email,
        password,
        ...(totp ? { totp } : {}),
      });
      signIn({ accessToken: result.access_token, user: result.user });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'two_factor_required') {
        setNeedsTotp(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else {
        setError(err instanceof ApiClientError ? err.message : 'Sign-in failed. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-12">
      <div className="w-full max-w-panel">
        <div className="mb-8 flex items-center gap-2.5">
          <LogoMark size={52} />
          <div className="leading-none">
            <p className="text-[15px] font-semibold tracking-tight">RiskPay</p>
            <p className="mt-1 text-2xs text-content-subtle">Quantum Treasury Core</p>
          </div>
        </div>

        <div className="panel p-6">
          <h1 className="text-lg font-semibold tracking-tight">Sign in</h1>
          <p className="mt-1.5 text-xs text-content-muted">Treasury, settlement engine and platform controls. Super Administrators only.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-content-muted">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-content-muted">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
              />
            </div>

            {needsTotp && (
              <div className="animate-slide-down">
                <label htmlFor="totp" className="mb-1.5 block text-xs font-medium text-content-muted">
                  Authentication code
                </label>
                <input
                  id="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="\d{6}"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                  className="input numeral tracking-[0.4em]"
                  autoFocus
                />
              </div>
            )}

            {error && (
              <p role="alert" className="rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-[#FDA4AF]">
                {error}
              </p>
            )}

            <button type="submit" className="btn btn-primary w-full" disabled={busy}>
              {busy ? <Spinner /> : <ArrowRight className="h-4 w-4" />}
              {busy ? 'Signing in' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 border-t border-line pt-4 text-2xs leading-relaxed text-content-faint">
            Staff accounts are provisioned by the platform administrator. Contact IT to reset a password or enrol a new
            authenticator device.
          </p>
        </div>

        <div className="mt-5 space-y-2">
          <p className="flex items-start gap-2 text-2xs leading-relaxed text-content-faint">
            <ShieldCheck className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            Sessions are held in this tab only and expire after 15 minutes of inactivity.
          </p>
          <p className="flex items-start gap-2 text-2xs leading-relaxed text-content-faint">
            <KeyRound className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            Every action you take here is written to the immutable audit log.
          </p>
        </div>
      </div>
    </div>
  );
}
